import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import relocationApi from '../services/relocationApi';
import { formatDate } from '../utils/dateFormatter';
import {
  RELOCATION_CAUTION,
  RELOCATION_MODAL_TITLE,
  RELOCATION_REASON_LABELS,
  RELOCATION_INELIGIBLE_LABELS,
  RELOCATION_NOT_ELIGIBLE_LABELS,
  RELOCATION_UI
} from '../config/relocationLabels';
import '../styles/RelocateGuestModal.css';

/**
 * «نقل الضيف» — the operator records a move they have already decided on.
 * tasks/guest-relocation-v1.md §4.4 (G2, L14) · Decisions: GUEST_RELOCATION_DESIGN.md
 *
 * ── The system executes and records; it never decides (L14) ───────────────────────────────────
 * There is no recommendation here, no "best match", no pre-selected unit. The list is a FILTER
 * that removes units which would be wrong, in a stable order a person can scan. Ranking them by
 * desirability would be the system choosing, and the whole design says a human does that.
 *
 * ── Ruled-out units are SHOWN, with the reason ────────────────────────────────────────────────
 * The tempting version hides them and offers a short clean list. That is worse: at 2am the
 * difference between "someone bought it" and "its lock is dead" sends the operator somewhere
 * completely different, and a list that silently drops half the estate teaches people not to
 * trust it. They are listed, quietly, below the choices.
 *
 * ── Refusals are shown verbatim ───────────────────────────────────────────────────────────────
 * The create call preserves the backend's message. Every refusal it can return is one the operator
 * must act on differently — the unit is sold, another guest is in it, the stay is over — and a
 * generic "could not save" would send them back round the same loop.
 */
const RelocateGuestModal = ({ show, onHide, booking, onRelocated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('not_ready');
  const [notes, setNotes] = useState('');

  // Every open starts clean. Carrying `reason` over is the dangerous one: the operator picked
  // «مشكلة في القفل» last time, closed without saving, and the next move files itself under a fault
  // that never happened — in the one field a future report groups by. `data` is cleared too, or the
  // period line shows the PREVIOUS booking's dates until the new fetch lands.
  useEffect(() => {
    if (!show || !booking?._id) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setSelected('');
    setData(null);
    setReason('not_ready');
    setNotes('');
    relocationApi.getCandidates(booking._id)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching relocation candidates:', err);
        setError(err.response?.data?.error || 'تعذّر جلب الوحدات المتاحة');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [show, booking?._id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;

    setSaving(true);
    setError(null);
    try {
      const res = await relocationApi.create({
        bookingId: booking._id,
        toPropertyId: selected,
        reason,
        notes: notes || undefined
      });
      if (res?.success) {
        onRelocated?.(res.allocation);
        onHide();
      } else {
        // A 200 that did not save. Without this the spinner just stops and the operator is left
        // looking at a modal that appears to have done nothing — and may press it again.
        setError(res?.error || 'تعذّر تسجيل النقل');
      }
    } catch (err) {
      // Verbatim: every refusal here means something different to the person reading it.
      setError(err.response?.data?.error || 'تعذّر تسجيل النقل');
    } finally {
      setSaving(false);
    }
  };

  const candidates = data?.candidates || [];
  const ineligible = data?.ineligible || [];
  // "There is no list" is a different answer from "the list is empty" — the booking is cancelled, or
  // the stay is already over. Neither is about finding a room.
  const notEligible = data?.notEligible || null;
  const answered = Boolean(data) && !loading;
  // Is there anything to record? A reason dropdown, a notes box and a submit button under "no unit
  // is available" is a form that cannot be filled in — it reads as though the operator has missed a
  // step, when the answer is simply that there is nowhere to put this guest.
  const canChoose = answered && !notEligible && candidates.length > 0;

  return (
    // Not dismissable mid-save: ESC, the backdrop and the X were all live during the POST, so the
    // request could finish against a closed modal — and a refusal would be set on something nobody
    // can see, then wiped by the next open.
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      size="lg"
      dir="rtl"
      className="relocate-modal"
      backdrop={saving ? 'static' : true}
      keyboard={!saving}
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{RELOCATION_MODAL_TITLE}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={submit}>
        <Modal.Body>
          {/* Before the picker, because it qualifies the whole action rather than the choice. The
              amber is the app's ATTENTION tone — the same one the relocation banner will wear on
              this booking afterwards — deliberately not the red that means failure: nothing has
              gone wrong by opening this. */}
          <div className="relocate-modal__caution" role="note">
            <p className="relocate-modal__caution-title">{RELOCATION_CAUTION.title}</p>
            <p className="relocate-modal__caution-line">{RELOCATION_CAUTION.when}</p>
            {/* Its own line, and the heavier of the two: this is the consequence operators do not
                expect — two units leave the calendar, not one. */}
            <p className="relocate-modal__caution-line relocate-modal__caution-line--cost">
              {RELOCATION_CAUTION.cost}
            </p>
          </div>

          <p className="relocate-modal__hint">{RELOCATION_UI.hint}</p>

          {answered && !notEligible && data.from && data.to && (
            <p className="relocate-modal__period">
              {RELOCATION_UI.period} {formatDate(data.from)} — {formatDate(data.to)}
            </p>
          )}

          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" size="sm" />
            </div>
          )}

          {answered && notEligible && (
            <Alert variant="secondary">
              {RELOCATION_NOT_ELIGIBLE_LABELS[notEligible] || RELOCATION_UI.noCandidates}
            </Alert>
          )}

          {/* Gated on an ANSWER, not on "not loading" — before the fetch begins both are false and
              this painted "no unit is available", the most alarming sentence in the modal, on every
              single open. */}
          {answered && !notEligible && candidates.length === 0 && (
            // Not an error — a real answer, and the operator's next move is to look outside the
            // system (decline, or sell a different night). Saying so plainly beats an empty list.
            <Alert variant="warning">{RELOCATION_UI.noCandidates}</Alert>
          )}

          {canChoose && (
            <Form.Group className="mb-3">
              <Form.Label id="relocate-unit-label">{RELOCATION_UI.chooseUnit}</Form.Label>
              <div className="relocate-modal__units" role="radiogroup" aria-labelledby="relocate-unit-label">
                {candidates.map((c) => (
                  <label
                    key={String(c.propertyId)}
                    className={`relocate-modal__unit ${selected === String(c.propertyId) ? 'is-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="relocation-unit"
                      value={String(c.propertyId)}
                      checked={selected === String(c.propertyId)}
                      onChange={(e) => setSelected(e.target.value)}
                    />
                    <span className="relocate-modal__unit-name">
                      {c.name}
                      {/* The code, under the name — it is what ops reads out on the phone, and unit
                          names are long and repeat («… (2)»). Quiet, because the name is still what
                          they scan by. */}
                      {c.propertyCode && (
                        <span className="relocate-modal__unit-code">{c.propertyCode}</span>
                      )}
                    </span>
                    {/* Same building first and marked — the easiest move is next door, but it is a
                        hint to a human, never a filter and never a recommendation. */}
                    {c.sameBuilding && (
                      <span className="relocate-modal__badge">{RELOCATION_UI.sameBuilding}</span>
                    )}
                    {/* Ranked, not just listed. The key handover decides whether the guest gets
                        through a door tonight; the Gathern block is paperwork that can follow. */}
                    {c.manualKeyHandover && (
                      <span className="relocate-modal__badge relocate-modal__badge--key">
                        {RELOCATION_UI.manualKeyHandover}
                      </span>
                    )}
                    {c.manualChannelBlock && (
                      <span className="relocate-modal__badge relocate-modal__badge--chore">
                        {RELOCATION_UI.manualChannelBlock}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </Form.Group>
          )}

          {canChoose && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>{RELOCATION_UI.reason}</Form.Label>
                <Form.Select value={reason} onChange={(e) => setReason(e.target.value)}>
                  {Object.entries(RELOCATION_REASON_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{RELOCATION_UI.notes}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={notes}
                  maxLength={2000}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Form.Group>
            </>
          )}

          {answered && ineligible.length > 0 && (
            <details className="relocate-modal__ruled-out">
              <summary>{RELOCATION_UI.ruledOut} ({ineligible.length})</summary>
              <ul>
                {ineligible.map((u) => (
                  <li key={String(u.propertyId)}>
                    <span className="relocate-modal__ruled-name">
                      {u.name}
                      {u.propertyCode && (
                        <span className="relocate-modal__unit-code">{u.propertyCode}</span>
                      )}
                    </span>
                    <span className="relocate-modal__ruled-reason">
                      {RELOCATION_INELIGIBLE_LABELS[u.reason] || u.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>إغلاق</Button>
          {canChoose && (
            <Button type="submit" variant="primary" disabled={!selected || saving}>
              {saving ? 'جاري التسجيل...' : RELOCATION_UI.confirm}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RelocateGuestModal;
